import React from 'react';
import { useLocation, Link } from 'react-router-dom';
import { useNavigation } from '../../contexts/NavigationContext';
import { useAuth } from '../../contexts/AuthContext';
import './Breadcrumbs.css';

interface BreadcrumbItem {
  path: string;
  label: string;
  icon?: React.ReactNode;
}

const Breadcrumbs: React.FC = () => {
  const location = useLocation();
  const { currentPath } = useNavigation();
  const { user, isAuthenticated } = useAuth();
  
  // Define breadcrumb mapping based on routes
  const getBreadcrumbs = (): BreadcrumbItem[] => {
    // If we have navigation context, use that
    if (currentPath.length > 0) {
      return currentPath.map(item => ({
        path: item.path || location.pathname,
        label: item.label,
        icon: item.icon
      }));
    }
    const pathSegments = location.pathname.split('/').filter(segment => segment);
    
    const breadcrumbMap: Record<string, BreadcrumbItem> = {
      '': {
        path: '/',
        label: 'Inicio',
        icon: (
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"></path>
          </svg>
        )
      },
      'chat': {
        path: '/chat',
        label: 'Chat',
        icon: (
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"></path>
          </svg>
        )
      },
      'admin': {
        path: '/admin',
        label: 'Administración',
        icon: (
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M12 1L3 5v6c0 5.55 3.84 10.74 9 12 5.16-1.26 9-6.45 9-12V5l-9-4z"></path>
          </svg>
        )
      },
      'analytics': {
        path: '/admin',
        label: 'Analytics',
        icon: (
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <polyline points="23 6 13.5 15.5 8.5 10.5 1 18"></polyline>
            <polyline points="17 6 23 6 23 12"></polyline>
          </svg>
        )
      },
      'prompts': {
        path: '/admin',
        label: 'Prompts',
        icon: (
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <polyline points="22 12 18 12 15 21 9 3 6 12 2 12"></polyline>
          </svg>
        )
      },
      'users': {
        path: '/admin',
        label: 'Usuarios',
        icon: (
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"></path>
            <circle cx="9" cy="7" r="4"></circle>
            <path d="M23 21v-2a4 4 0 0 0-3-3.87"></path>
            <path d="M16 3.13a4 4 0 0 1 0 7.75"></path>
          </svg>
        )
      }
    };

    const breadcrumbs: BreadcrumbItem[] = [];
    
    // Always add home
    breadcrumbs.push(breadcrumbMap['']);
    
    // Add segments
    let currentSegmentPath = '';
    for (const segment of pathSegments) {
      currentSegmentPath += `/${segment}`;
      if (breadcrumbMap[segment]) {
        breadcrumbs.push({
          ...breadcrumbMap[segment],
          path: currentSegmentPath
        });
      }
    }
    
    return breadcrumbs;
  };

  const breadcrumbs = getBreadcrumbs();

  // Keep a simple header in chat for non-admin users, without route breadcrumbs.
  if (location.pathname === '/chat' && isAuthenticated && !user?.is_admin) {
    return (
      <nav className="breadcrumbs">
        <div className="breadcrumbs-inner chat-header-inner">
          <span className="breadcrumb-current chat-header-title">
            <span className="breadcrumb-icon">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"></path>
              </svg>
            </span>
            <span className="breadcrumb-label">Chat</span>
          </span>
        </div>
      </nav>
    );
  }

  // Don't show breadcrumbs on login or single-level pages
  if (location.pathname === '/login' || breadcrumbs.length <= 1) {
    return null;
  }

  return (
    <nav className="breadcrumbs">
      <div className="breadcrumbs-inner">
        {breadcrumbs.map((item, index) => (
          <div key={item.path} className="breadcrumb-item">
            {index > 0 && (
              <svg 
                className="breadcrumb-separator" 
                width="16" 
                height="16" 
                viewBox="0 0 24 24" 
                fill="none" 
                stroke="currentColor" 
                strokeWidth="2"
              >
                <polyline points="9 18 15 12 9 6"></polyline>
              </svg>
            )}
            {index === breadcrumbs.length - 1 ? (
              <span className="breadcrumb-current">
                {item.icon && <span className="breadcrumb-icon">{item.icon}</span>}
                <span className="breadcrumb-label">{item.label}</span>
              </span>
            ) : (
              <Link to={item.path} className="breadcrumb-link">
                {item.icon && <span className="breadcrumb-icon">{item.icon}</span>}
                <span className="breadcrumb-label">{item.label}</span>
              </Link>
            )}
          </div>
        ))}
      </div>
    </nav>
  );
};

export default Breadcrumbs;